import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import createMarkup from '../../../js/utils/formatters/html-to-text-formatter';

import SectionWrapper from '../../common/SectionWrapper';
import StepByStepGuideBlock from '../../blocks/StepByStepGuideBlock';
import HeroBlock from '../../blocks/HeroBlock';
import NewFormBlock from '../../blocks/NewFormBlock';
import ImageBlock from '../../blocks/ImageBlock';
import USPBlock from '../../blocks/USPBlock';
import Reapply from '../../../js/forms/GenericComponents/Reapply/Reapply';
import { useOptions } from '../../../context/options';

import useDevice from '../../../js/utils/use-device';
import devices from '../../../js/styles/Devices';
import style from './style';
import createConfig from './config';
import dynamicEvaluation from '../../utils/dynamic-config-checker';
import { brand } from '../../../js/localizations/current-locale';
import ABTestAboveFold from '../AboveFoldWithFormWithoutHeroSection/ABTestAboveFold';

const FormBlock = React.lazy(() => import('../../blocks/FormBlock'));

const Text = styled.div`
  && {
    font-size: var(--text-font-size);
    font-family: var(--font-family);
    display: block;
    position: relative;
    z-index: 1;

    p {
      font-size: var(--text-font-size);
    }

    a {
      color: var(--link-color);
      margin: 1rem 0;
    }

    h1 {
      letter-spacing: var(--has-letter-spacing);
    }

    h2, h2.span {
      font-family: var(--font-family);
      font-size: var(-h2-font-size);
      font-weight: var(--has-font-weight);
      letter-spacing: var(--has-letter-spacing);
    }

    @media ${devices.upFromTablet} {
      margin: ${style.textMargin};
    }

    @media ${devices.downFromTablet} {
      h1 {
        font-size: var(--h1-mobile-font-size);
      }
    }
  }
`;

const TextMobile = styled.div`
  color: ${style.textMobileColor};
  padding-bottom: 3rem;
  margin: auto 4%;

  @media ${devices.upFromTablet} {
      display: none;
    }
`;

const Title = styled.h1`
  font-size: var(--h4-desktop-font-size);
  font-family: 'Lexend'!important;

  @media ${devices.downFromMobile} {
    display: none;
  }
`;

const Subtitle = styled.h2`
  max-width: 34rem;
  color: #E0E0E0;
  font-family: 'Lexend-Regular'!important;
  font-size: 20px!important;
  font-style: normal;
  font-weight: 400!important;
  line-height: 26px;

  @media ${devices.aboveFoldWithFormCustomBreakpointBetweenTabletAndMobile} {
    display: none;
  }

  @media ${devices.downFromTablet} {
    max-width: 350px;
    font-size: 1rem!important;
  }
`;

const HeadTitle = styled.h1`
  color: #FFF;
  font-family: 'Lexend'!important;
  font-size: 24px!important;
  font-style: normal;
  font-weight: 600!important;
  line-height: 115%;
  letter-spacing: 0.48px;

  @media ${devices.upFromMobile} {
    display: none;
  }

  @media ${devices.downFromMobile} {
   margin: 4%!important;
  }
`;

const LayoutWrapper = styled.div`
  && {
    padding: ${style.layoutPadding};
    margin: 0 auto;
    position: relative;
    display: flex;
    display: -ms-flexbox;
    flex-direction: row;
    flex-wrap: nowrap;
    column-gap: 1rem;
    max-width: ${style.maxWrapperWidth};

    @media ${devices.downFromDesktop} {
      flex-direction: column;
      margin: 0 auto;
      padding: 1rem;
      max-width: ${style.maxWrapperWidthDownFromDesktop};
    }

    @media ${devices.upFromLargeDesktop} {
      max-width: ${style.maxWrapperWidthUpFromLargeDesktop};
    }

    @media ${devices.downFromTablet} {
      margin-top: ${style.layoutWrapperMarginTopTablet};
    }

    @media ${devices.downFromMobile} {
      margin-top: ${style.layoutWrapperMarginTopMobile};
    }

  }
`;

const BackgroundLayer = styled.div`
  && {
    ${(props) => props.whichBrand !== 'advisa' && `
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
    `}

    min-height: ${style.bgLayerHeight};
    overflow: hidden;
    background: ${(props) => props.backgroundColor};
    background-size: cover;
    background-image: ${(props) => props.backgroundImage};

    @media ${devices.downFromTablet}{
      background-image: ${(props) => props.mobileBackgroundImage};
    }
  }
`;

const PositionWrapper = styled.div`
  && {
    position: ${(props) => props.position};
  }
`;

const HeroLayer = styled.div`
  && {
    position: relative;

    @media ${devices.downFromMobile} {
      > div {
        position: initial;
        padding: 1rem 0;

        > div {
          position: unset!important;
        }
      }

      h1 {
        margin: 4%;
      }

      h2 {
        bottom: 12rem;
      }
    }

    h1 {
      padding-left: ${style.desktopTitlePadding};
      position: absolute;
      bottom: 5rem;
      left: 5%;
      color: var(--white);
    }

    h2 {
      padding-left: ${style.desktopTitlePadding};
      position: absolute;
      bottom: 1rem;
      left: 5%;
      color: var(--white);
    }

    @media ${devices.downFromTablet} {
      max-height: 500px;

      h1 {
        bottom: 18rem;
      }

      h2 {
        bottom: 12rem;
      }
    }

    @media ${devices.aboveFoldWithFormCustomBreakpointBetweenTabletAndMobile} {
      h1 {
        bottom: 11rem;
      }
    }
  }
`;

const CenteredFormTitle = styled.div`
  && {
    text-align: center;
    color: ${(props) => props.titleColor};
    position: relative;
    z-index: 1;
    padding-top: ${style.topTitlePadding};
    text-shadow: ${style.textShadow};

    h1 {
      font-weight: var(--has-font-weight);
      font-family: var(--title-font);
    }

    h2 {
      font-family: var(--font-family);
      font-weight: var(--has-font-weight);
    }

    @media ${devices.downFromTablet} {
      padding: 2.5rem 1rem 0;

      h1 {
        font-size: var(--h1-mobile-font-size);
      }
    }
  }
`;

const CenteredForSubtitle = styled.h2`
  && {
    font-family: var(--font-family);
    font-size: var(--h2-desktop-font-size);
    font-weight: var(--has-font-weight);
    line-height: 1.25;
    color: ${(props) => props.titleColor};

    @media ${devices.downFromTablet} {
      font-weight: var(--has-no-font-weight);
    }
  }
  `;

const FlexCol = styled.div`
  && {
    flex: 1;
    -ms-flex: 1;
    flex-basis: ${(props) => props.flexSize}!important;
    max-height: fit-content;
    margin-top: ${(props) => (props.float ? '-24rem' : 'none')};

    @media ${devices.downFromDesktop} {
      flex: 1 1 auto;
      margin-top: 0;
    }

    // Force children to have width: auto
    & > * > .onb .advisa {
      width: auto!important;
      p {
        font-size: inherit !important;
      }
    }

    & > * > .onb .sambla {
      width: auto!important;
    }
  }
`;

const Foreground = styled.img`
  && {
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    margin: 0 auto;
    height: 50rem;
    width: auto;
    transform: ${style.foregroundTranslate};

    @media ${devices.downFromDesktop} {
      display: none;
    }
  }
`;

const MobileForeground = styled.img`
  && {
    margin: 0 auto;
    height: ${style.mobileForegroundHeight};
    width: ${style.mobileForegroundWidth};;
    margin-bottom: -7px;
    z-index: 0;
    transform: ${style.foregroundMobileTranslate};

    @media ${devices.upFromTablet} {
      display: none;
    }

    @media ${devices.mobile} {
      display: ${style.mobileForegroundDisplay};
    }
  }
`;

const Campaign = styled.div`
  && {
    margin: 0 auto;

    @media ${devices.downFromDesktop} {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-bottom: 2rem;
    }
  }
`;

const AboveFoldWithFormSection = ({ acf }) => {
  const { isLargeMobile, isTablet, isTabletLargerThenTablet } = useDevice();

  const {
    optionCampaigns,
  } = useOptions();
  const campaign = optionCampaigns?.campaigns?.find((c) => c.name === acf.campaignName);
  const config = createConfig(acf, style, campaign);
  const isRahalaitos = brand === 'rahalaitos';
  const isOmalaina = brand === 'omalaina';
  const indexPage = typeof window !== 'undefined' && window.location.pathname === '/';
  // eslint-disable-next-line react/no-array-index-key
  const ExperimentRunning = useFeatureIsOn('omalaina_IndexSwap');

  const slideComponents = {
    NewFormBlock,
    FormBlock,
    Text,
    MobileForeground,
    StepByStepGuideBlock,
    Campaign,
    USPBlock,
    Reapply,
    TextMobile,
    HeadTitle,
  };

  const isNewForm = Array.isArray(acf?.form?.type) && acf.form.type[0] === 'newForm';

  return ExperimentRunning && indexPage && isOmalaina ? (
    <ABTestAboveFold />
  ) : (
    <SectionWrapper
      settings={acf.sectionSettings}
      noContainer
      disablePadding
    >
      <PositionWrapper position={config.general.wrapperPosition}>
        <BackgroundLayer
          whichBrand={brand}
          backgroundColor={config.general.hero.backgroundColor}
          backgroundImage={config.general.hero.fullBackgroundImage}
          mobileBackgroundImage={config.general.hero.fullMobileBackgroundImage}
        >

          {config.general.hero
            && (
            <HeroLayer>
              {isTablet
                && <ImageBlock imageObject={config.general.hero.mobileBackgroundImage} />}
              { isTabletLargerThenTablet
                && <ImageBlock imageObject={config.general.hero.backgroundImage} />}
              {config.general.hero.title
                && <Title dangerouslySetInnerHTML={createMarkup(config.general.hero.title)} />}
              {config.general.hero.subtitle
                && (
                <Subtitle
                  dangerouslySetInnerHTML={createMarkup(config.general.hero.subtitle)}
                />
                )}
              {isLargeMobile && (
                config.general.hero.trustpilotWidget
              )}
            </HeroLayer>
            )}
          {config.general.hero.foregroundImage
            && <Foreground src={config.general.hero.foregroundImage} />}
          {config.general.hero.mobileForegroundImage
          && <MobileForeground src={config.general.hero.mobileForegroundImage} />}
        </BackgroundLayer>

        {(isRahalaitos || isOmalaina)
          && (
          <CenteredFormTitle
            titleColor={acf.sectionSettings.titleColor}
          >
            <h1 dangerouslySetInnerHTML={createMarkup(acf.desktopHero.title)} />
            <CenteredForSubtitle
              dangerouslySetInnerHTML={createMarkup(acf.desktopHero.subtitle)}
              titleColor={acf.sectionSettings.titleColor}
            />
            <p dangerouslySetInnerHTML={createMarkup(acf.pitchText)} />
          </CenteredFormTitle>
          )}

        <LayoutWrapper>
          {config.columns.map((col, key) => (
            <FlexCol flexSize={col.flexSize || '100%'} float={col.float} key={key}>
              {col.components.map((item, itemKey) => {
                const FormComponent = slideComponents[item.component];
                // Conditionally render NewFormBlock or FormBlock based on isNewForm
                if (item.component === 'NewFormBlock' && !isNewForm) {
                  return null; // Skip rendering NewFormBlock if isNewForm is false
                }
                if (item.component === 'FormBlock' && isNewForm) {
                  return null; // Skip rendering FormBlock if isNewForm is true
                }

                return (
                  <>
                    {dynamicEvaluation(item.props.showComponent, { campaign })
                        && (
                        <Suspense fallback={<div>Loading...</div>}>
                          <FormComponent key={`${item.component}_${itemKey}`} {...item.acf} {...item.props} />
                        </Suspense>
                        )}
                  </>
                );
              })}
            </FlexCol>

          ),
          )}
        </LayoutWrapper>
      </PositionWrapper>
    </SectionWrapper>
  );
};

AboveFoldWithFormSection.propTypes = {
  acf: PropTypes.shape({
    mobileHero: PropTypes.shape(HeroBlock.propTypes).isRequired,
    desktopHero: PropTypes.shape(HeroBlock.propTypes).isRequired,
    pitchText: PropTypes.string,
    pitchTextMobile: PropTypes.string,
    guideSteps: PropTypes.shape(StepByStepGuideBlock.propTypes),
    sectionSettings: SectionWrapper.propTypes.settings,
    form: PropTypes.shape(FormBlock.propTypes).isRequired,
    fadingBackground: PropTypes.bool,
    campaignName: PropTypes.string,
    stepper: PropTypes.shape(USPBlock.propTypes.items),
  }).isRequired,
};

export default AboveFoldWithFormSection;
